import {
  Check,
  ChevronLeft,
  ChevronRight,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useState } from "react";
import { Button } from "../../../../../components/ui/button";
import { Card } from "../../../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { DocumentForApproval } from "../types";
import {
  applyCnpjMask,
  cleanCnpj,
} from "../../../../../utils/format-utils";
import { MissingFieldsAlert } from "../../../components/missing-fields-alert";
import {
  APPROVAL_REQUIRED_FIELDS,
  getMissingRequiredFields,
} from "../../../utils/required-fields";

interface DocumentDetailDialogProps {
  document: DocumentForApproval | null;
  open: boolean;
  onClose: () => void;
  onApprove: (document: DocumentForApproval) => void;
  onReject: (document: DocumentForApproval) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

const autoFormatValue = (key: string, value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "Não informado";
  }
  const stringValue = String(value);
  const keyLower = key.toLowerCase();

  if (
    (keyLower.includes("date") ||
      keyLower.includes("birthday") ||
      keyLower.includes("birth") ||
      keyLower.includes("nascimento")) &&
    stringValue.includes("-") &&
    stringValue.match(/^\d{4}-\d{2}-\d{2}/)
  ) {
    try {
      const [year, month, day] = stringValue.split("-");
      const d = new Date(Number(year), Number(month) - 1, Number(day));
      return d.toLocaleDateString("pt-BR");
    } catch (e) {
      return stringValue;
    }
  }

  if (
    (keyLower.includes("phone") ||
      keyLower.includes("tel") ||
      keyLower.includes("fone")) &&
    stringValue.replace(/\D/g, "").length >= 10
  ) {
    const digits = stringValue.replace(/\D/g, "");
    if (digits.length === 11) {
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    } else if (digits.length === 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    }
  }

  if (
    keyLower.includes("cpf") &&
    stringValue.replace(/\D/g, "").length === 11
  ) {
    const digits = stringValue.replace(/\D/g, "");
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  if (keyLower.includes("cnpj") && cleanCnpj(stringValue).length === 14) {
    return applyCnpjMask(cleanCnpj(stringValue));
  }

  if (
    (keyLower.includes("cep") || keyLower.includes("zip")) &&
    stringValue.replace(/\D/g, "").length === 8
  ) {
    const digits = stringValue.replace(/\D/g, "");
    return digits.replace(/(\d{5})(\d{3})/, "$1-$2");
  }

  if (stringValue.includes("@") && stringValue.includes(".")) {
    return stringValue.toLowerCase();
  }

  return stringValue;
};

const translateKey = (key: string): string => {
  const translations: Record<string, string> = {
    fullName: "Nome Completo",
    nome_completo: "Nome Completo",
    ops: "Operação",
    personType: "Tipo de Pessoa",
    cnpj: "CNPJ",
    cpf: "CPF",
    franchise: "Franquia",
    pixKey: "Chave PIX",
    pixKeyType: "Tipo de Chave PIX",
    phone: "Telefone",
    telefone: "Telefone",
    birthday: "Data de Nascimento",
    data_nascimento: "Data de Nascimento",
    email: "Email",
    cep: "CEP",
    uf: "Estado",
    city: "Cidade",
    neighborhood: "Bairro",
    address: "Endereço",
    addressNumber: "Número",
    lgpdAgreeId: "ID Acordo LGPD",
    lgpdAgreeBucket: "Bucket LGPD",
    lgpdAgreeKeyname: "Arquivo LGPD",
    companyName: "Nome Empresa",
  };

  return (
    translations[key] ||
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  );
};

export function DocumentDetailDialog({
  document,
  open,
  onClose,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: DocumentDetailDialogProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  if (!document) return null;

  const missingFields = getMissingRequiredFields(
    document,
    APPROVAL_REQUIRED_FIELDS
  );

  const images = [
    { url: document.signedUrlCNH, title: "Documento (frente)" },
    { url: document.signedUrlCNH2, title: "Documento (verso)" },
    { url: document.signedUrlSelfie, title: "Selfie" },
  ].filter((img) => img.url);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setZoom(1);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setZoom(1);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
  };

  const renderSection = (
    title: string,
    keys: string[],
    data: DocumentForApproval
  ) => {
    const orderedKeys = keys.filter((key) => data.hasOwnProperty(key));
    if (orderedKeys.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3 pb-2 border-b">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {orderedKeys.map((key) => (
            <div
              key={key}
              className="bg-muted/50 p-3 rounded-md border min-h-[80px] flex flex-col flex-wrap"
            >
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                {translateKey(key)}
              </p>
              <div className="text-sm flex-1 items-center max-w-[300px] break-words text-wrap">
                {autoFormatValue(key, data[key])}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const personalInfoKeys = [
    "fullName",
    "nome_completo",
    "cpf",
    "cnpj",
    "birthday",
    "data_nascimento",
    "email",
    "phone",
    "telefone",
    "personType",
    "pixKey",
    "pixKeyType",
    "franchise",
    "ops",
  ];

  const addressInfoKeys = [
    "city",
    "uf",
    "address",
    "addressNumber",
    "cep",
    "neighborhood",
  ];

  const lgpdInfoKeys = ["lgpdAgreeId", "lgpdAgreeBucket", "lgpdAgreeKeyname"];

  const excludedKeys = [
    "id",
    "signedUrlCNH",
    "signedUrlCNH2",
    "signedUrlSelfie",
    "cnhPic",
    "selfie",
    ...personalInfoKeys,
    ...addressInfoKeys,
    ...lgpdInfoKeys,
  ];

  const otherKeys = Object.keys(document).filter(
    (key) => !excludedKeys.includes(key)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes do Documento</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <MissingFieldsAlert
            missingFields={missingFields}
            description="Busque estes dados com o motorista antes de aprovar o cadastro. A aprovação continua liberada, mas o campo ficará vazio no sistema."
          />

          <Card className="p-4">
            {renderSection("Informações Pessoais", personalInfoKeys, document)}
            {renderSection(
              "Informações de Endereço",
              addressInfoKeys,
              document
            )}
            {renderSection("Informações LGPD", lgpdInfoKeys, document)}
            {renderSection("Outras Informações", otherKeys, document)}
          </Card>

          {images.length > 0 && (
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-4">Imagens</h3>
              <div className="space-y-4">
                <div className="relative bg-muted rounded-lg overflow-hidden">
                  <div className="flex items-center justify-center min-h-[400px] overflow-auto">
                    <img
                      src={images[currentImageIndex].url}
                      alt={images[currentImageIndex].title}
                      style={{ transform: `scale(${zoom})` }}
                      className="max-w-full h-auto transition-transform duration-200"
                    />
                  </div>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleZoomIn}
                      disabled={zoom >= 3}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleZoomOut}
                      disabled={zoom <= 1}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                  </div>
                  {images.length > 1 && (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute left-2 top-1/2 -translate-y-1/2"
                        onClick={handlePrevImage}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        onClick={handleNextImage}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {images[currentImageIndex].title} ({currentImageIndex + 1}/
                    {images.length})
                  </div>
                </div>
              </div>
            </Card>
          )}

          <div className="flex justify-center gap-4">
            <Button
              variant="destructive"
              onClick={() => onReject(document)}
              disabled={isApproving || isRejecting}
              className="min-w-[120px]"
            >
              <X className="h-4 w-4 mr-2" />
              Rejeitar
            </Button>
            <Button
              variant="default"
              onClick={() => onApprove(document)}
              disabled={isApproving || isRejecting}
              className="min-w-[120px] bg-green-600 hover:bg-green-700"
            >
              <Check className="h-4 w-4 mr-2" />
              Aprovar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
